import { createStore } from "./utils";

const CURRENT_VERSION = 2;

export const DEFAULT_DASHBOARD_CONFIGURATION = {
  showAnalytics: true,
  showMessages: true,
  showTickets: true,
  showRecentActivity: true,
  showCallHoldQueues: true,
  showTicketQueues: true,
} as const satisfies DashboardConfiguration;

interface DashboardConfiguration {
  showAnalytics: boolean;
  showMessages: boolean;
  showTickets: boolean;
  showRecentActivity: boolean;
  showCallHoldQueues: boolean;
  showTicketQueues: boolean;
}

interface DashboardConfigurationStore {
  userConfigurations: Record<string, DashboardConfiguration>;
  updateConfiguration: (userId: string, newConfiguration: Partial<DashboardConfiguration>) => void;
}

export const { useStore: useDashboardConfigurationStore, useShallowStore: useShallowDashboardConfigurationStore } =
  createStore<DashboardConfigurationStore>(
    (set) => ({
      userConfigurations: {},
      updateConfiguration: (userId, newConfiguration) =>
        set((state) => {
          const existingConfiguration = state.userConfigurations[userId] ?? DEFAULT_DASHBOARD_CONFIGURATION;
          return {
            ...state,
            userConfigurations: {
              ...state.userConfigurations,
              [userId]: {
                ...existingConfiguration,
                ...newConfiguration,
              },
            },
          };
        }),
    }),
    {
      persist: "localStorage",
      name: "DASHBOARD_CONFIGURATION",
      version: CURRENT_VERSION,
      migrate: (persistedState, version) => {
        type V0State = Omit<DashboardConfigurationStore, "userConfigurations"> & {
          userConfigurations: Record<string, Omit<DashboardConfiguration, "showCallHoldQueues">>;
        };

        if (version !== CURRENT_VERSION) {
          const state = persistedState as V0State;
          const migratedConfigurations: Record<string, DashboardConfiguration> = Object.fromEntries(
            Object.entries(state.userConfigurations).map(([userId, config]) => [
              userId,
              {
                ...DEFAULT_DASHBOARD_CONFIGURATION,
                ...config,
              },
            ])
          );

          return { ...state, userConfigurations: migratedConfigurations };
        }

        // All migrations have been applied, so the data matches the current schema
        return persistedState as DashboardConfigurationStore;
      },
    }
  );
